  import React, { useEffect, useMemo, useState } from "react";
  import { 
    HiChevronRight, 
    HiDownload, 
    HiFilter,
    HiRefresh
  } from "react-icons/hi";
  import {
    FiBarChart2,
    FiPieChart,
    FiTrendingUp,
    FiTrendingDown,
    FiMessageSquare,
    FiClock,
    FiAlertCircle,
    FiCheckCircle,
    FiSend,
    FiEye,
    FiEyeOff
  } from "react-icons/fi";
  import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid
  } from "recharts";
  import Apilogoschartui from "../../DynamicComponents/Apilogochartui";
  import apiEndpoints from "../../apiconfig";

  const COLORS = ["#10B981", "#3B82F6", "#EF4444", "#F59E0B", "#8B5CF6"];

  const UserApiReport = () => {
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({ sent: 0, delivered: 0, read: 0, failed: 0 });
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [filterType, setFilterType] = useState("7days");
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [showDetailedView, setShowDetailedView] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("all");

    // Fetch Logs
    const fetchLogs = async () => {
      try {
        setLoading(true);
        let url = `${apiEndpoints.Reports}`;
        const params = [];
        if (fromDate) params.push(`fromDate=${encodeURIComponent(fromDate)}`);
        if (toDate) params.push(`toDate=${encodeURIComponent(toDate)}`);
        if (filterType) params.push(`filterType=${encodeURIComponent(filterType)}`);
        if (params.length > 0) url += `?${params.join("&")}`;
        
        const token = localStorage.getItem("token");

        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const json = await res.json();
        if (json.status) {
          setData(json.data || []);
          if (json.summary) setSummary(json.summary);
        }
      } catch (err) {
        console.error("Failed to fetch logs:", err);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchLogs();
    }, []);

    // Enhanced Analytics Data
    const analyticsData = useMemo(() => {
      const statusCount = {
        delivered: data.filter(d => d.status === "delivered").length,
        read: data.filter(d => d.status === "read").length,
        sent: data.filter(d => d.status === "sent").length,
        failed: data.filter(d => d.status === "failed").length
      };

      // Optimized hourly data for mobile - group by 4-hour intervals on small screens
      const hourlyData = Array.from({ length: 24 }, (_, hour) => {
        const hourData = data.filter(item => {
          const itemHour = new Date(item.timestamp * 1000).getHours();
          return itemHour === hour;
        });
        
        return {
          hour: `${hour}:00`,
          shortHour: `${hour}h`,
          delivered: hourData.filter(d => d.status === "delivered").length,
          read: hourData.filter(d => d.status === "read").length,
          sent: hourData.filter(d => d.status === "sent").length,
          failed: hourData.filter(d => d.status === "failed").length
        };
      });

      const dailyData = data.reduce((acc, item) => {
        const date = new Date(item.timestamp * 1000).toLocaleDateString();
        if (!acc[date]) {
          acc[date] = { date, delivered: 0, read: 0, sent: 0, failed: 0 };
        }
        acc[date][item.status]++;
        return acc;
      }, {});

      const categoryData = data.reduce((acc, item) => {
        const category = item.category || "Unknown";
        if (!acc[category]) {
          acc[category] = { name: category, value: 0 };
        }
        acc[category].value++;
        return acc;
      }, {});

      return {
        statusCount,
        hourlyData,
        dailyData: Object.values(dailyData).slice(-30),
        categoryData: Object.values(categoryData),
        totalMessages: data.length,
        successRate: ((statusCount.delivered + statusCount.read) / data.length * 100) || 0,
        failureRate: (statusCount.failed / data.length * 100) || 0
      };
    }, [data]);

    // Filtered data based on status
    const filteredData = useMemo(() => {
      if (selectedStatus === "all") {
        return showDetailedView ? data : data.slice(0, 10);
      }
      const statusFiltered = data.filter(item => item.status === selectedStatus);
      return showDetailedView ? statusFiltered : statusFiltered.slice(0, 10);
    }, [data, selectedStatus, showDetailedView]);

    // Enhanced Summary Statistics
    const summaryStats = useMemo(() => [
      { 
        label: "TOTAL MESSAGES", 
        value: analyticsData.totalMessages, 
        color: "#2563eb",
        icon: <FiMessageSquare className="text-xl" />,
        trend: "+12%",
        change: "vs last period"
      },
      { 
        label: "SUCCESS RATE", 
        value: `${analyticsData.successRate.toFixed(1)}%`, 
        color: "#059669",
        icon: <FiCheckCircle className="text-xl" />,
        trend: "+5.2%",
        change: "improvement"
      },
      { 
        label: "FAILED MESSAGES", 
        value: analyticsData.statusCount.failed, 
        color: "#dc2626",
        icon: <FiAlertCircle className="text-xl" />,
        trend: "-2.1%",
        change: "vs last period"
      },
      { 
        label: "AVG RESPONSE TIME", 
        value: "2.3s", 
        color: "#d97706",
        icon: <FiClock className="text-xl" />,
        trend: "-0.4s",
        change: "faster"
      }
    ], [analyticsData]);

    // Status filter configuration
    const statusFilters = [
      { key: "all", label: "All Messages", count: data.length, color: "gray" },
      { key: "delivered", label: "Delivered", count: analyticsData.statusCount.delivered, color: "emerald" },
      { key: "read", label: "Read", count: analyticsData.statusCount.read, color: "blue" },
      { key: "sent", label: "Sent", count: analyticsData.statusCount.sent, color: "amber" },
      { key: "failed", label: "Failed", count: analyticsData.statusCount.failed, color: "red" }
    ];

    const handleResetFilters = () => {
      setFromDate("");
      setToDate("");
      setFilterType("7days");
      setSelectedStatus("all");
      setShowDetailedView(false);
    };

    const handleExportData = async (format = 'csv') => {
      setExportLoading(true);
      try {
        let content, mimeType, filename;
        
        if (format === 'csv') {
          const headers = ["S.No", "Message ID", "Message", "Recipient", "Status", "Category", "Timestamp"];
          const csvContent = [
            headers.join(","),
            ...data.map((item, index) => [
              index + 1,
              item.message_id,
              `"${(item.message_text || "-").replace(/"/g, '""')}"`,
              item.recipient_id,
              item.status,
              item.category || "Unknown",
              new Date(item.timestamp * 1000).toISOString()
            ].join(","))
          ].join("\n");
          
          content = csvContent;
          mimeType = "text/csv";
          filename = `whatsapp-messages-${new Date().toISOString().split('T')[0]}.csv`;
        } else {
          const exportData = {
            exportDate: new Date().toISOString(),
            summary: analyticsData.statusCount,
            messages: data.map(item => ({
              ...item,
              formattedTime: new Date(item.timestamp * 1000).toISOString()
            }))
          };
          content = JSON.stringify(exportData, null, 2);
          mimeType = "application/json";
          filename = `whatsapp-messages-${new Date().toISOString().split('T')[0]}.json`;
        }

        const blob = new Blob([content], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Export failed:", error);
      } finally {
        setExportLoading(false);
      }
    };

    // Status chip component
    const StatusChip = ({ status }) => {
      const statusConfig = {
        delivered: {
          bg: "bg-emerald-50",
          text: "text-emerald-700",
          border: "border-emerald-200",
          icon: <FiCheckCircle className="w-3 h-3" />,
        },
        read: {
          bg: "bg-blue-50",
          text: "text-blue-700",
          border: "border-blue-200",
          icon: <FiEye className="w-3 h-3" />,
        },
        sent: {
          bg: "bg-amber-50",
          text: "text-amber-700",
          border: "border-amber-200",
          icon: <FiSend className="w-3 h-3" />,
        },
        failed: {
          bg: "bg-red-50",
          text: "text-red-700",
          border: "border-red-200",
          icon: <FiAlertCircle className="w-3 h-3" />,
        },
        received: {
          // ✅ NEW
          bg: "bg-purple-50",
          text: "text-purple-700",
          border: "border-purple-200",
          icon: <FiMessageSquare className="w-3 h-3" />,
        },
      };

      const config = statusConfig[status] || statusConfig.received;

      return (
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${config.bg} ${config.text} ${config.border}`}>
          {config.icon}
          <span className="text-xs font-medium capitalize">{status}</span>
        </div>
      );
    };

    // Custom responsive legend for mobile
    const CustomizedLegend = (props) => {
      const { payload } = props;
      return (
        <div className="flex flex-wrap justify-center gap-3 mt-4 px-2">
          {payload.map((entry, index) => (
            <div key={`legend-${index}`} className="flex items-center gap-2 text-xs">
              <div 
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-gray-600">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    };

    // Custom tooltip for better mobile experience
    const CustomTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg max-w-[200px]">
            <p className="font-semibold text-sm mb-2">{label}</p>
            {payload.map((entry, index) => (
              <p key={index} className="text-xs" style={{ color: entry.color }}>
                {entry.name}: <span className="font-semibold">{entry.value}</span>
              </p>
            ))}
          </div>
        );
      }
      return null;
    };

    const normalizeStatus = (status) => {
      if (!status || status === "null") return "received";
      return status.toLowerCase();
    };


    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6 font-['Montserrat']">
        {/* Main Container */}
        <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">WhatsApp Analytics Dashboard</h1>
              <div className="flex items-center text-sm text-gray-600">
                <span className="text-gray-500">Home</span>
                <HiChevronRight className="mx-2 text-gray-400" />
                <span className="text-emerald-600 font-medium">Message Analytics</span>
              </div>
            </div>

          <div className="flex flex-wrap gap-2 sm:gap-3">
            {/* <button
              onClick={() => setShowDetailedView(!showDetailedView)}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 font-medium text-xs sm:text-sm"
            >
              {showDetailedView ? <FiEyeOff className="text-sm" /> : <FiEye className="text-sm" />}
              {showDetailedView ? "Compact" : "Detailed"}
            </button> */}
            <button
              onClick={() => handleExportData('csv')}
              disabled={exportLoading}
              className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 font-medium text-xs sm:text-sm disabled:opacity-50"
            >
              <HiDownload className="text-sm" />
              {exportLoading ? "Exporting..." : "Export"}
            </button>
          </div>
        </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {summaryStats.map((stat, index) => (
              <div
                key={stat.label}
                className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${stat.color}15`, color: stat.color }}
                  >
                    {stat.icon}
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full ${
                      stat.trend.startsWith('+') || !stat.trend.includes('%') && !stat.trend.includes('s') 
                        ? 'bg-emerald-50 text-emerald-600' 
                        : 'bg-red-50 text-red-600'
                    }`}>
                      {stat.trend.startsWith('+') || !stat.trend.includes('%') ? <FiTrendingUp className="w-2 h-2 sm:w-3 sm:h-3" /> : <FiTrendingDown className="w-2 h-2 sm:w-3 sm:h-3" />}
                      <span className="text-xs">{stat.trend}</span>
                    </span>
                  </div>
                </div>
                <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-1">
                  {stat.value}
                </div>
                <div className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
                  {stat.label}
                </div>
                <div className="text-xs text-gray-500">
                  <span className="font-medium">{stat.change}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Status Overview */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Message Status Overview</h2>
                <p className="text-xs sm:text-sm text-gray-500">Filter messages by delivery status</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
              {statusFilters.map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setSelectedStatus(filter.key)}
                  className={`p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                    selectedStatus === filter.key
                      ? filter.color === 'emerald' ? 'border-emerald-500 bg-emerald-50 text-emerald-600'
                      : filter.color === 'blue' ? 'border-blue-500 bg-blue-50 text-blue-600'
                      : filter.color === 'amber' ? 'border-amber-500 bg-amber-50 text-amber-600'
                      : filter.color === 'red' ? 'border-red-500 bg-red-50 text-red-600'
                      : 'border-gray-500 bg-gray-50 text-gray-600'
                      : 'border-gray-200 bg-white text-gray-900 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg sm:text-xl font-bold mb-1">
                    {filter.count}
                  </div>
                  <div className="text-xs font-medium leading-tight">
                    {filter.label}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <HiFilter className="text-emerald-600 text-sm sm:text-base" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900">Report Filters</h2>
                  <p className="text-xs sm:text-sm text-gray-500">Customize your analytics view</p>
                </div>
              </div>
              <button
                onClick={handleResetFilters}
                className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <HiRefresh className="w-3 h-3 sm:w-4 sm:h-4" />
                Reset All
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="space-y-2">
                <label className="block text-xs sm:text-sm font-medium text-gray-700">From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 sm:py-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 text-sm"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-xs sm:text-sm font-medium text-gray-700">To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 sm:py-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 text-sm"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-xs sm:text-sm font-medium text-gray-700">Time Range</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 sm:py-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 text-sm"
                >
                  <option value="24hours">Last 24 Hours</option>
                  <option value="7days">Last 7 Days</option>
                  <option value="30days">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
              
              <div className="flex flex-col justify-end">
                <button
                  onClick={fetchLogs}
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg transition-all duration-200 font-medium text-xs sm:text-sm disabled:opacity-50"
                >
                  <HiRefresh className={loading ? "animate-spin w-3 h-3 sm:w-4 sm:h-4" : "w-3 h-3 sm:w-4 sm:h-4"} />
                  {loading ? "Updating..." : "Apply Filters"}
                </button>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          {/* Charts Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Delivery Trends Chart */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-6 shadow-xs">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                    <FiBarChart2 className="text-blue-600" size={18} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Delivery Trends</h3>
                    <p className="text-sm text-gray-500">Message status distribution over time</p>
                  </div>
                </div>
              </div>
              <div className="w-full h-80">
                <Apilogoschartui data={analyticsData.dailyData} tableData={data} />
              </div>
            </div>

            {/* Message Distribution Chart */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-6 shadow-xs">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                    <FiPieChart className="text-purple-600" size={18} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Message Distribution</h3>
                    <p className="text-sm text-gray-500">By category and status</p>
                  </div>
                </div>
              </div>
              <div className="w-full h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip 
                      formatter={(value, name) => [
                        <span key="value" className="font-semibold">{value} messages</span>,
                        name
                      ]}
                    />
                  
                    <Pie
                      data={analyticsData.categoryData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={100}
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                      {analyticsData.categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Hourly Performance */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                  <FiClock className="text-orange-600 text-sm sm:text-base" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Hourly Performance</h3>
                  <p className="text-xs sm:text-sm text-gray-500">Message activity by hour of day</p>
                </div>
              </div>
            </div>
            <div className="w-full h-64 sm:h-72 lg:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={analyticsData.hourlyData}
                  margin={{
                    top: 10,
                    right: window.innerWidth < 640 ? 10 : 30,
                    left: window.innerWidth < 640 ? 0 : 20,
                    bottom: window.innerWidth < 640 ? 10 : 20,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey={window.innerWidth < 640 ? "shortHour" : "hour"}
                    interval={window.innerWidth < 640 ? 3 : 0}
                    angle={window.innerWidth < 640 ? -45 : 0}
                    textAnchor={window.innerWidth < 640 ? "end" : "middle"}
                    height={window.innerWidth < 640 ? 60 : 40}
                    fontSize={window.innerWidth < 640 ? 10 : 12}
                  />
                  <YAxis fontSize={window.innerWidth < 640 ? 10 : 12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend content={<CustomizedLegend />} />
                  <Bar dataKey="delivered" fill="#10B981" name="Delivered" />
                  <Bar dataKey="read" fill="#3B82F6" name="Read" />
                  <Bar dataKey="sent" fill="#F59E0B" name="Sent" />
                  <Bar dataKey="failed" fill="#EF4444" name="Failed" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

        {/* Message Logs Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-50 rounded-lg flex items-center justify-center">
                  <FiMessageSquare className="text-gray-600 text-sm sm:text-base" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                    Message Logs {selectedStatus !== "all" && `- ${selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)}`}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Showing {filteredData.length} of {data.length} messages
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* <button
                  onClick={() => handleExportData('json')}
                  className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50"
                >
                  <HiDownload className="w-4 h-4" />
                  Export JSON
                </button> */}
              </div>
            </div>
            
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full min-w-full border-collapse text-xs sm:text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap text-xs">
                      #
                    </th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap text-xs min-w-[80px] sm:min-w-[120px]">
                      Message ID
                    </th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap text-xs min-w-[100px] sm:min-w-[150px]">
                      Content
                    </th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap text-xs">
                      Recipient
                    </th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap text-xs">
                      Status
                    </th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap text-xs">
                      Timestamp
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredData.length > 0 ? (
                    filteredData.map((item, index) => (
                      <tr key={item.message_id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-600 font-medium whitespace-nowrap">
                          {index + 1}
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-900 font-medium whitespace-nowrap truncate max-w-[80px] sm:max-w-[120px]">
                          {item.message_id}
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-600 max-w-[100px] sm:max-w-[150px] truncate">
                          {item.message_text || "No content"}
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-600 font-mono text-xs whitespace-nowrap">
                          {item.recipient_id}
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                          <StatusChip status={normalizeStatus(item.status)} />
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-500 text-xs whitespace-nowrap">
                          {new Date(item.timestamp * 1000).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-2 sm:px-4 py-4 sm:py-8 text-center text-gray-500 text-xs sm:text-sm">
                        No messages found for the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              
              {data.length > 0 && (
                <div className="bg-gray-50 border-t border-gray-200 px-3 sm:px-6 py-3 sm:py-4">
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-3">
                    <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                      {showDetailedView 
                        ? `Showing all ${filteredData.length} messages` 
                        : `Showing ${filteredData.length} of ${selectedStatus === "all" ? data.length : analyticsData.statusCount[selectedStatus]} messages`
                      }
                    </p>
                    <button
                      onClick={() => setShowDetailedView(!showDetailedView)}
                      className="text-xs sm:text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 bg-white border border-emerald-200 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg hover:bg-emerald-50 transition-colors"
                    >
                      {showDetailedView ? (
                        <>
                          <FiEyeOff className="w-3 h-3 sm:w-4 sm:h-4" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <FiEye className="w-3 h-3 sm:w-4 sm:h-4" />
                          Show All
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

  export default UserApiReport;